import { ProfilePhoto, Interest } from '../components/Profile/types';

export type OnboardingStepId = 
  | 'welcome'
  | 'assessment'
  | 'persona-confirmation'
  | 'photos'
  | 'profile'
  | 'interests'
  | 'preferences'
  | 'app-tour'
  | 'completion';

export interface OnboardingStep {
  id: OnboardingStepId;
  title: string;
  description: string;
  canSkip: boolean;
  isRequired: boolean;
  order: number;
}

export interface OnboardingProgress {
  currentStep: OnboardingStepId;
  completedSteps: OnboardingStepId[];
  skippedSteps: OnboardingStepId[];
  stepData: Partial<Record<OnboardingStepId, any>>;
}

export interface OnboardingData {
  persona?: {
    selectedPersona?: string;
    personaTraits?: string[];
    personaResponses?: Record<string, string>;
    personaConfidence?: number;
  };
  photos: ProfilePhoto[];
  bio: {
    content: string;
    characterCount: number;
    lastUpdated?: Date;
  };
  occupation?: string;
  education?: string;
  location?: {
    city: string;
    state: string;
    country: string;
    /** Present only when the browser granted geolocation. */
    coordinates?: { lat: number; lng: number };
  };
  interests: Interest[];
  preferences: {
    ageRange: {
      min: number;
      max: number;
    };
    maxDistance: number;
    showMe: string[];
  };
  appTourViewed: boolean;
}

export interface OnboardingStepProps {
  data: any;
  onChange: (data: any, isValid?: boolean) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
  setNextOverride?: (handler: (() => void) | null) => void;
  setBackOverride?: (handler: (() => void) | null) => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Plenty of Hearts',
    description: 'Let\'s get you started!',
    canSkip: false,
    isRequired: true,
    order: 1
  },
  {
    id: 'assessment',
    title: 'Heart Assessment',
    description: 'Discover your dating persona',
    canSkip: true,
    isRequired: false,
    order: 2
  },
  {
    id: 'persona-confirmation',
    title: 'Confirm Your Persona',
    description: 'A quick question about your style',
    canSkip: true,
    isRequired: false,
    order: 3
  },
  // REQUIRED, and not skippable. A main photo and a 15-second intro video are
  // what every other member sees before deciding anything: the swipe card, the
  // feeds, Explore's grid and the profile drawer are all built from them. An
  // account that skips this finishes onboarding invisible — it appears in
  // nobody's deck with a face, and the person who skipped has no idea why
  // nothing is happening.
  //
  // The step already refuses to validate without both. This is what stops Next
  // and Skip from stepping over that refusal.
  {
    id: 'photos',
    title: 'Add Your Photo and Video',
    description: 'A main photo and a 15-second intro video are both required',
    canSkip: false,
    isRequired: true,
    order: 4
  },
  {
    id: 'profile',
    title: 'Complete Your Profile',
    description: 'Tell others about yourself',
    canSkip: true,
    isRequired: false,
    order: 5
  },
  {
    id: 'interests',
    title: 'Select Your Interests',
    description: 'Choose at least 3 interests',
    canSkip: true,
    isRequired: false,
    order: 6
  },
  // REQUIRED, and not skippable. Who you want to see is not a nicety: it is
  // the one hard filter every people-listing surface applies. Skipped, it
  // leaves gender_preference NULL, and every one of those filters then takes
  // its "no preference expressed" branch and shows the user everyone — which
  // is how an account can finish onboarding and be dealt every gender on the
  // platform.
  {
    id: 'preferences',
    title: 'Set Your Preferences',
    description: 'Tell us who you want to meet',
    canSkip: false,
    isRequired: true,
    order: 7
  },
  {
    id: 'app-tour',
    title: 'App Tour',
    description: 'Learn about key features',
    canSkip: true,
    isRequired: false,
    order: 8
  },
  {
    id: 'completion',
    title: 'You\'re All Set!',
    description: 'Start exploring and connecting',
    canSkip: false,
    isRequired: true,
    order: 9
  }
];
